<template>
  <VanPopup
    :show="show"
    class="update-publish-modal h-full"
    @update:show="emit('update:show', $event)"
    position="bottom"
    round
    teleport="body"
    @click-overlay="handleCancel"
    safe-area-inset-top
    closeable
  >
    <div class="px-4 pt-4">
      <h3 class="text-lg font-semibold text-base-content">发布新版本</h3>
    </div>
    <van-form @submit="handleSubmit" ref="formRef">
      <div class="p-4 space-y-4">
        <!-- Version Number -->
        <van-cell-group>
          <van-field
            v-model="form.VersionName"
            label="版本号"
            placeholder="请输入版本号，如：1.0.27"
            :rules="[
              { required: true, message: '请输入版本号' },
              {
                pattern: /^\d+\.\d+\.\d+$/,
                message: '版本号格式不正确，请使用 x.y.z 格式',
              },
            ]"
            clearable
          />

          <van-field
            v-model.number="form.VersionCode"
            type="number"
            label="版本代码"
            placeholder="请输入版本代码，如：32"
            :rules="[
              { required: true, message: '请输入版本代码' },
              { validator: (value) => value > 0, message: '版本代码必须大于0' },
            ]"
            clearable
          />
        </van-cell-group>

        <!-- Update Content -->
        <van-cell-group>
          <van-field
            v-model="form.ModifyContent"
            type="textarea"
            label="更新内容"
            placeholder="请输入本次更新的详细内容..."
            :rules="[{ required: true, message: '请输入更新内容' }]"
            :maxlength="500"
            show-word-limit
            autosize
            rows="4"
          />
        </van-cell-group>

        <!-- APK File Upload -->
        <van-cell-group>
          <van-field
            v-model="form.DownloadUrl"
            label="下载链接"
            placeholder="请输入APK下载链接"
            :rules="[
              { required: true, message: '请输入下载链接' },
              {
                pattern: /^https?:\/\/.+/,
                message: '请输入有效的HTTP/HTTPS链接',
              },
            ]"
            clearable
          />

          <van-field
            v-model.number="form.ApkSize"
            type="number"
            label="文件大小"
            placeholder="请输入APK文件大小"
            :rules="[
              { required: true, message: '请输入文件大小' },
              { validator: (value) => value > 0, message: '文件大小必须大于0' },
            ]"
            clearable
          >
            <template #right-icon>
              <span class="text-sm text-base-content/60">KB</span>
            </template>
          </van-field>
          <van-field
            v-model="form.ApkMd5"
            label="文件MD5"
            placeholder="请输入APK文件MD5"
            clearable
          />
        </van-cell-group>

        <!-- Update Settings -->
        <van-cell-group>
          <van-cell title="更新" label="开启后用户必须更新才能继续使用">
            <template #right-icon>
              <van-switch
                v-model="form.UpdateStatus"
                :active-value="1"
                :inactive-value="undefined"
                size="20px"
              />
            </template>
          </van-cell>
        </van-cell-group>

        <!-- Preview Card -->
        <div class="bg-base-200 rounded-lg p-4 space-y-2">
          <h4 class="text-sm font-semibold text-base-content/70">预览</h4>
          <div class="bg-base-100 rounded-lg p-3 space-y-2">
            <div class="flex justify-between items-center">
              <span class="text-sm text-base-content/70">版本号:</span>
              <span class="text-sm font-mono"
                >v{{ form.VersionName || "0.0.0" }}</span
              >
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-base-content/70">文件大小:</span>
              <span class="text-sm">{{ form.ApkSize || 0 }} KB</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-sm text-base-content/70">自动更新:</span>
              <span class="text-sm">{{ form.UpdateStatus ? "是" : "否" }}</span>
            </div>
          </div>
        </div>
      </div>
    </van-form>

    <div class="flex gap-2 p-4">
      <button
        type="button"
        class="btn btn-outline flex-1"
        @click="handleCancel"
        :disabled="isSubmitting"
      >
        取消
      </button>
      <button
        type="button"
        class="btn btn-primary flex-1"
        @click="handleSubmit"
        :disabled="isSubmitting"
      >
        <span
          v-if="isSubmitting"
          class="loading loading-spinner loading-sm mr-2"
        ></span>
        {{ isSubmitting ? "发布中..." : "发布" }}
      </button>
    </div>
  </VanPopup>
</template>

<script lang="ts" setup>
import { ref, reactive } from "vue";
import { showToast } from "vant";
import { AndroidVersion } from "@/types/entity";
import api from "@/modules/api";

// interface AndroidVersion {
//   UpdateStatus: number;
//   VersionName: string;
//   VersionCode: number;
//   ModifyContent: string;
//   DownloadUrl: string;
//   ApkSize: number;
//   ApkMd5: string;
// }

interface Props {
  show: boolean;
  initialData?: Partial<AndroidVersion>;
}

withDefaults(defineProps<Props>(), {
  show: false,
  initialData: () => ({}),
});

const emit = defineEmits<{
  (event: "update:show", value: boolean): void;
  (event: "cancel"): void;
}>();

// Form data
const form = reactive<Partial<AndroidVersion>>({
  VersionName: "",
  VersionCode: 0,
  ModifyContent: "",
  DownloadUrl: "https://www.bingciyuan.top/app-release/bingciyuan.apk",
  ApkSize: 0,
  UpdateStatus: 1,
  ApkMd5: "",
});

// Form validation
const formRef = ref();
const isSubmitting = ref(false);

// Form submission
const handleSubmit = async () => {
  try {
    await formRef.value?.validate();
    isSubmitting.value = true;

    // 这里调用发布接口
    await publishUpdate();

    showToast("版本发布成功！");
    emit("update:show", false);
  } catch (error) {
    console.error("发布失败:", error);
    showToast("发布失败，请检查表单信息");
  } finally {
    isSubmitting.value = false;
  }
};

const handleCancel = () => {
  emit("cancel");
  emit("update:show", false);
};

// 模拟发布接口
const publishUpdate = async () => {
  // 这里替换为真实的API调用
  return api.createOneAndroidVersion(form);
};
</script>
